import React, { useEffect, useState } from 'react';

/**
 * FishingAnimation Component
 * Displays fishing animation using a 128x128 sprite sheet (4x4 grid, 32x32 frames)
 * Row 0: Down, Row 1: Up, Row 2: Right, Row 3: Left
 * Each row has 4 frames for the fishing motion
 */
const FishingAnimation = ({ x, y, direction, duration = 8000, zIndex = 100, onComplete }) => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [animationPhase, setAnimationPhase] = useState('fishing'); // 'fishing' or 'idle'

  // Direction to row mapping
  const directionToRow = {
    down: 0,
    up: 1,
    right: 2,
    left: 3
  };

  const row = directionToRow[direction] || 0;

  useEffect(() => {
    const timers = [];
    const frameInterval = 300; // 300ms per frame
    
    // Phase 1: Cast down - animate through frames 0->1->2->3 (1.2 seconds)
    for (let i = 0; i <= 3; i++) {
      timers.push(setTimeout(() => {
        setCurrentFrame(i);
      }, i * frameInterval));
    }
    
    // Phase 2: Return to rest - animate back 3->2->1->0 (1.2 seconds)
    const castDuration = 1200; // 4 frames * 300ms
    const returnStart = castDuration;
    
    for (let i = 3; i >= 0; i--) {
      timers.push(setTimeout(() => {
        setCurrentFrame(i);
        if (i === 0) {
          setAnimationPhase('waiting');
        }
      }, returnStart + ((3 - i) * frameInterval)));
    }
    
    // Phase 3: Wait at frame 0 (4.4 seconds)
    const returnDuration = 1200; // 4 frames * 300ms
    const waitDuration = duration - castDuration - returnDuration - 1200; // Reserve 1.2s for pull up
    
    // Phase 4: Pull up - animate through frames 0->1->2->3 (1.2 seconds)
    const pullStart = castDuration + returnDuration + waitDuration;
    
    for (let i = 0; i <= 3; i++) {
      timers.push(setTimeout(() => {
        setCurrentFrame(i);
        if (i === 3) {
          setAnimationPhase('complete');
        }
      }, pullStart + (i * frameInterval)));
    }

    // Complete animation
    timers.push(setTimeout(() => {
      if (onComplete) {
        onComplete();
      }
    }, duration));

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]); // Only depend on duration, not onComplete

  // Calculate sprite position
  const frameX = currentFrame * 32;
  const frameY = row * 32;

  return (
    <div
      style={{
        position: 'absolute',
        left: `${x}px`,
        top: `${y}px`,
        width: '32px',
        height: '32px',
        backgroundImage: 'url(/Maps/fishing/fishing.png)',
        backgroundPosition: `-${frameX}px -${frameY}px`,
        backgroundSize: '128px 128px',
        imageRendering: 'pixelated',
        zIndex: zIndex,
        pointerEvents: 'none'
      }}
    />
  );
};

export default FishingAnimation;
